import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { AxiosResponse } from 'axios';
import { PurchaseOrderGetMinDto } from '../../../../types/PurchaseOrderGetMinDto';
import { purchaseOrderGetMinToFormik } from '../../../../types/convertors/purchaseOrderGetMinToFormik';
import { purchaseOrderGetMinToEmptyFormik } from '../../../../types/convertors/purchaseOrderGetMinToEmptyFormik';

import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import PurchaseOrdersApi from '../../../../services/api/PurchaseOrdersApi';
import FormRow from '../../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import AutocompleteComponent from '../../../../components/common/forms/Autcomplete/AutocompleteComponent';
import { API_URL, SHIPPING_TYPES, ROLES } from '../../../../utils/constants';
import AutocompleteStaticComponent from '../../../../components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { constantsToAutocomplete } from '../../../../utils/misc';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';

import TertiaryButton from '../../../../components/common/forms/Button/TertiaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import PurchaseSecondaryNavigation from '~/src/components/common/navigation/PurchaseSecondaryNavigation';
import DatePickerInputComponent from '~/src/components/common/forms/DatePickerInput/DatePickerInputComponent';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import RolesDependent from '~/src/components/common/RolesDependent';
import SuppliersApi from '~/src/services/api/SuppliersApi';
import { set } from 'lodash';

interface ParamProps {
  id?: string;
}

export default function PurchaseOrderEdit(): React.ReactElement {
  const { id } = useParams<ParamProps>();
  const [itemData, setItemData] = useState<PurchaseOrderGetMinDto | null>(null);
  const history = useHistory();
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [supplierUrl, setSupplierUrl] = useState(`${API_URL}/contractors/suppliers/get`);

  const saveItem = async (values: any, setSubmitting: (isSubmitting: boolean) => void) => {
    setSubmitting(true);
    const containerId = values.containerId || null;
    const convertedValues = {
      ...values,
      containerId,
      warehousesIds: Array.isArray(values.warehouses)
        ? values.warehouses.map((item: any) => item.id).join(',')
        : null,
    };

    let result: AxiosResponse<PurchaseOrderGetMinDto> | null = null;

    if (id) {
      result = await PurchaseOrdersApi.update(id, convertedValues);
    } else {
      result = await PurchaseOrdersApi.create(convertedValues);
    }

    if (result.status === StatusCodes.OK) {
      const orderId = result.data?.id;
      history.push(`/purchase-orders/details/${orderId}`);
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async (orderId: string): Promise<void> => {
    const result = await PurchaseOrdersApi.get(orderId);
    setItemData(result.data);
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, [id]);

  const handleBack = () => {
    history.push('/orders');
  };

  const fetchAvailableSuppliers = async (warehouseOwnerId: string, setFieldValue: (field: string, value: any) => void) => {
    if (warehouseOwnerId === "") {
      setAvailableSuppliers([]);
      setFieldValue('supplierId', '');
    } else {
      try {
        const response = await SuppliersApi.getAvailableSupppliers(warehouseOwnerId);

        if (response.data.data.length > 0) {
          setAvailableSuppliers(response.data.data);
        } else {
          setAvailableSuppliers([]);
        }
      } catch (error) {
        setSupplierUrl(`${API_URL}/contractors/suppliers/get`);
      }
    }
  };

  const getInitialValues = (() => {
    if (!id) {
      const iv = purchaseOrderGetMinToEmptyFormik();
      return iv;
    } else {
      if (itemData) {
        const conv = purchaseOrderGetMinToFormik(itemData);
        delete conv.customerId;
        conv.supplierId = itemData?.supplierId ?? '';
        return conv;
      } else {
        return null;
      }
    }
  })();

  return (
    <>
      <WhitePad>
        <PurchaseSecondaryNavigation />

        <TitleComponent
          title={`Purchase ${id ? 'Edit ' : 'Create '} Order ${id ? '#' + itemData?.orderNumber : ''}`}
        />

        {getInitialValues && (
          <Formik
            initialValues={getInitialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              saveItem(values, setSubmitting);
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              errors,
              touched,
              initialValues,
            }) => {
              useEffect(() => {
                fetchAvailableSuppliers(values.warehouseOwnerId, setFieldValue);
              }, [values.warehouseOwnerId]);

              return (
                <FormWrapper width="100%">
                  <form onSubmit={handleSubmit} autoComplete="off">
                    <FormRow>
                      <RolesDependent
                        roles={[
                          ROLES.ADMIN,
                          ROLES.SUPER_ADMIN,
                          ROLES.WAREHOUSE_ADMIN,
                          ROLES.WAREHOUSE,
                          ROLES.OFFICE,
                          ROLES.SALES,
                          ROLES.DATABASE_ADMIN,
                        ]}
                      >
                        <AutocompleteComponent
                          url={`${API_URL}/contractors/warehouse-owners/get`}
                          name="warehouseOwnerId"
                          label="warehouseOwnerId"
                          initialValue={values.warehouseOwnerId}
                          onChange={setFieldValue}
                          getOptionLabel={(option) => `${option.name}`}
                          isMulti={false}
                        />
                      </RolesDependent>
                      <AutocompleteComponent
                        key={values.supplierId}
                        url={(availableSuppliers.length === 0 || values.warehouseOwnerId === "")
                          ? supplierUrl
                          : `${API_URL}/contractors/suppliers/get-available-suppliers/${values.warehouseOwnerId}`}
                        name="supplierId"
                        label="supplierId"
                        initialValue={values.supplierId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        errorMessage={errors.supplierId}
                        errors={errors}
                      />
                      <AutocompleteComponent
                        url={`${API_URL}/products/pallets/get`}
                        name="palletId"
                        label="palletId"
                        initialValue={values.palletId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        cols={3}
                        errorMessage={errors.palletId}
                        errors={errors}
                      />
                      <AutocompleteComponent
                        url={`${API_URL}/warehouses/get`}
                        name="warehouses"
                        label="Warehouses"
                        initialValue={values.warehouses}
                        onChange={setFieldValue}
                        queryFields={['name']}
                        getOptionLabel={(option) => `${option.name}`}
                        isMulti={true}
                        errorMessage={errors.warehouses}
                        cols={3}
                      />
                    </FormRow>
                    <FormRow>
                      <AutocompleteComponent
                        url={`${API_URL}/products/containers/get`}
                        name="containerId"
                        label="containerId"
                        initialValue={values.containerId}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.name}`}
                        queryFields={['name']}
                        disabled={values.shippingType === 'Air'}
                        errorMessage={errors.containerId}
                        errors={errors}
                      />
                      <AutocompleteStaticComponent
                        options={constantsToAutocomplete(SHIPPING_TYPES)}
                        name="shippingType"
                        label="shippingType"
                        initialValue={values.shippingType}
                        onChange={setFieldValue}
                        getOptionLabel={(option) => `${option.label}`}
                        cols={3}
                        errorMessage={errors.shippingType}
                        errors={errors}
                      />
                    </FormRow>

                    <FormRow>
                      <TextInputComponent
                        name="palletMaxWidth"
                        label="Pallet Max Width (cm)"
                        value={values.palletMaxWidth}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="number"
                        cols={3}
                        disabled={values.shippingType === 'Sea'}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="palletMaxHeight"
                        label="Pallet Max Height (cm)"
                        value={values.palletMaxHeight}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        disabled={values.shippingType === 'Sea'}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="palletMaxLength"
                        label="Pallet Max Length (cm)"
                        value={values.palletMaxLength}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        disabled={values.shippingType === 'Sea'}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="maxKg"
                        label="Pallet Max Weight (kg)"
                        value={values.maxKg}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="pickUpAddress"
                        label="pickUpAddress"
                        value={values.pickUpAddress}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="palletsDescription"
                        label="Pallets Description"
                        value={values.palletsDescription}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        errors={errors}
                      />

                      <DatePickerInputComponent
                        name="dateAvailable"
                        label="Date Available"
                        value={values.dateAvailable}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        onChange={(e): void => {
                          setFieldValue('dateAvailable', e.value);
                        }}
                        defaultValue={initialValues.dateAvailable}
                        cols={3}
                        errors={errors}
                      />
                      <TextInputComponent
                        name="grossWeightKg"
                        label="Gross Weight (kg)"
                        value={values.grossWeightKg}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={3}
                        type="number"
                        errors={errors}
                      />
                    </FormRow>
                    <div className="d-flex justify-content-between">
                      <TertiaryButton onClick={handleBack} title="Back" />
                      <PrimaryButton isSubmitting={isSubmitting} type="submit" title="Next" />
                    </div>
                  </form>
                </FormWrapper>
              );
            }}
          </Formik>
        )}
      </WhitePad>
    </>
  );
}